/**
 * Which zustand stores hold the signed-in person's state (SA-922 spec §1.3).
 *
 * Every module in src/ that creates a store is named here exactly once:
 * - RESET_TO_INITIAL: back to `getInitialState()` on an identity change.
 * - OWN_RESET: the store's own reset.
 * - KEPT: left alone, with the reason.
 *
 * `services/identityChange.ts` runs the resets. `identityScope.test.ts`
 * fails on a store this file does not name, so classify a new store in the
 * PR that adds it.
 */
import { useAppStore } from './appStore'
import { useApprovalToastStore } from './approvalToastStore'
import { useAvatarStore } from './avatarStore'
import { useChatStore } from './chatStore'
import { useComposerStore } from './composerStore'
import { useCopilotStore } from './copilotStore'
import { useCrmStore } from './crmStore'
import { useDashboardStore } from './dashboardStore'
import { useFeedbackContextStore } from './feedbackContextStore'
import { useHelpFeedbackStore } from './helpFeedbackStore'
import { useInboxStore } from './inboxStore'
import { useMarketingStore } from './marketingStore'
import { useNavBadgeStore } from './navBadgeStore'
import { usePermissionStore } from './permissionStore'
import { usePersonaStore } from './personaStore'
import { useProcessConfigStore } from './processConfigStore'
import { useProcessDraftStore } from './processDraftStore'
import { useRecordUpdateNoticeStore } from './recordUpdateNoticeStore'
import { useSettingsStore } from './settingsStore'
import { useTourStore } from './tourStore'
import { useVoiceStore } from './voiceStore'
import { useWorkflowConfigStore } from './workflowConfigStore'

/** What a reset needs from a zustand store, whatever its state type. Method
 * syntax keeps the parameter bivariant, so every bound store fits. */
export interface ResettableStore {
  getState(): object
  getInitialState(): object
  setState(state: object, replace: true): void
}

/** Back to the store's initial state, whole. */
export const RESET_TO_INITIAL: Readonly<Record<string, ResettableStore>> = {
  'stores/approvalToastStore.ts': useApprovalToastStore,
  'stores/avatarStore.ts': useAvatarStore,
  'stores/chatStore.ts': useChatStore,
  'stores/composerStore.ts': useComposerStore,
  'stores/copilotStore.ts': useCopilotStore,
  'stores/crmStore.ts': useCrmStore,
  'stores/dashboardStore.ts': useDashboardStore,
  // What a feedback report may say about the last ten minutes belongs to
  // the person who did it (SA-1304).
  'stores/feedbackContextStore.ts': useFeedbackContextStore,
  // The open panel and what someone was telling us are theirs alone (SA-1310).
  'stores/helpFeedbackStore.ts': useHelpFeedbackStore,
  // Includes the bulk triage run slice (SA-1329): a run never outlives its person.
  'stores/inboxStore.ts': useInboxStore,
  'stores/marketingStore.ts': useMarketingStore,
  'stores/navBadgeStore.ts': useNavBadgeStore,
  'stores/personaStore.ts': usePersonaStore,
  'stores/processConfigStore.ts': useProcessConfigStore,
  'stores/processDraftStore.ts': useProcessDraftStore,
  'stores/recordUpdateNoticeStore.ts': useRecordUpdateNoticeStore,
  // `loadSettings` MERGES over what is there, so a person with no saved
  // settings would otherwise inherit the previous person's. The reset also
  // puts `_settingsLoaded` and `_settingsSaveable` back to false, so the sync
  // cannot save until the new identity's own settings have loaded.
  'stores/settingsStore.ts': useSettingsStore,
  // A page tour belongs to the person taking it (SA-1315).
  'stores/tourStore.ts': useTourStore,
  'stores/voiceStore.ts': useVoiceStore,
  'stores/workflowConfigStore.ts': useWorkflowConfigStore,
}

/** A store whose reset is its own. */
export const OWN_RESET: Readonly<Record<string, () => void>> = {
  'stores/permissionStore.ts': () => usePermissionStore.getState().clear(),
  // Only the preferences `loadPreferences` writes per person. The theme is
  // NOT reset: `localStorage` is its source of truth and a backend theme is
  // never adopted while a local value exists, so resetting it could only
  // flip the UI to the page-load value (SA-922 item 1).
  'stores/appStore.ts': () => {
    const initial = useAppStore.getInitialState()
    useAppStore.setState({
      activePersonaId: initial.activePersonaId,
      isSidebarCollapsed: initial.isSidebarCollapsed,
    })
  },
}

/** Left alone on an identity change, and why. */
export const KEPT: Readonly<Record<string, string>> = {
  'stores/authStore.ts': 'the identity itself; the identity-change subscription reads it',
  'stores/featureStore.ts': "the deployment's feature manifest, the same for everyone",
  'stores/helpFeedbackBridge.ts':
    'whether the Help & feedback host is mounted, and an open request consumed at once; no identity data',
  'stores/feedbackStore.ts':
    'a device-local queue persisted across reloads; its cross-identity sync is out of scope (SA-922 spec §5)',
}
